import {
	useGridApiRef,
	GridToolbarContainer,
	GridToolbarExportContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton
} from '@mui/x-data-grid-premium';
import React, { useState, useMemo } from 'react';
import { Dialog, Divider, Card, CardContent, CardHeader, TablePagination, MenuItem, Button, Typography, Box } from '@mui/material';
import { useUniqueEffect } from '../hooks';
import { StyledDataGrid } from '../components/grid';
import { usePrerequisites } from '../contexts/Prerequisites';

const CustomeToolBar = ({ onExport }) => {
	const [{ strings }] = usePrerequisites();
	return (
		<GridToolbarContainer
			sx={{
				'& .filter': {
					backgroundColor: '#A49F9D'
				},
				justifyContent: 'flex-start'
			}}>
			<GridToolbarColumnsButton slotProps={{ button: { variant: 'contained', color: 'primary' } }} />
			<GridToolbarFilterButton slotProps={{ button: { variant: 'contained', className: 'filter' } }} />
			<GridToolbarExportContainer slotProps={{ button: { variant: 'contained', color: 'warning' } }}>
				<MenuItem onClick={() => onExport('excel')}>{strings.Excel}</MenuItem>
				<MenuItem onClick={() => onExport('csv')}>{strings.CSV}</MenuItem>
			</GridToolbarExportContainer>
		</GridToolbarContainer>
	)
}
const AdminLogsTable = ({ data, openModal, hideModal, columns, gridName, title, showHuluMsg }) => {
	const apiRef = useGridApiRef();
	const [{ strings }] = usePrerequisites();
	const [state, setState] = useState({
		currentPage: 0,
		pageSize: 50,
	})


	const handlePageChange = (event, newPage) => {
		setState(prev => {
			return { ...prev, currentPage: newPage }
		})
	}
	const handleRowsPerPage = (event) => {
		setState(prev => {
			return { ...prev, pageSize: event.target.value, currentPage: 0 }
		})
	}

	const handleExport = (format) => {
		const fileName = gridName === 'ItemSync' ? 'Scopes' : 'Users';
		const params = {
			fileName: `${fileName}_DuplicateRecords`,
		};
		if (format === 'csv') {
			apiRef.current?.exportDataAsCsv(params);
		} else if (format === 'excel') {
			apiRef.current?.exportDataAsExcel(params);
		}
	};


	useUniqueEffect(() => {
		if (openModal) {
			setState((prev) => ({ ...prev, pageSize: 50, currentPage: 0 }))
		}
	}, [openModal])

	const minHeight = useMemo(() => {
		const toolbarHeight = 34;
		const paginationHeight = 52;
		const headerHeight = 40;
		const rowHeight = 36;
		const rowSpan = !data.length ? rowHeight * 2 : 0;
		return toolbarHeight + headerHeight + rowSpan + paginationHeight;
	}, [data.length]);

	const gridColumns = useMemo(() => {
		return columns.map(column => ({
			...column,
			headerName: strings[column.headerName] || strings[column.field]
		}));
	}, []);

	return <Dialog
		open={openModal}
		onClose={hideModal}
		maxWidth="md"
		sx={{
			'& .MuiDialog-container': {
				'& .MuiPaper-root': {
					width: '100%',
					maxWidth: '900px',
				},
			},
			'& .MuiDialog-paper': {
				overflow: 'visible !important'  // Allow content to overflow
			},
			'& .MuiDataGrid-panel': {
				position: 'absolute !important',
				left: '0 !important',  // Align to left
				right: 'auto !important',
				transform: 'none !important',
				marginTop: '40px !important',
				zIndex: 9999
			},
			'& .MuiDataGrid-filterPanel': {
				position: 'relative !important',
				inset: 'auto !important',
				width: '320px !important' // Set specific width for filter panel
			},
			'& .MuiDataGrid-columnsPanel': {
				position: 'absolute !important',
				zIndex: 9999,
				backgroundColor: 'background.paper',
				boxShadow: 24,
				width: '240px !important'
			}
		}
		}>
		<Card>
			<CardHeader
				title={title || strings.Duplicaterecords}
				action={<Button
					onClick={hideModal}
					title={strings.Close}
					color="error"
					variant="contained"
					size="small"
					sx={{
						px: 1,
						fontSize: 14,
						textTransform: 'uppercase',
						fontWeight: 'bold',
					}}
				>
					{strings.Close}
				</Button>}
				sx={{ color: 'primary.main', display: 'flex', justifyContent: 'space-between' }}>
			</CardHeader>
			<Divider varient="fullwidth" />
			{showHuluMsg && <Typography variant="body2" sx={{ marginLeft: '20px' }}>{
				(strings.NonHuluInsertionMessage || '').split('\n').map((line, index) => (
					<React.Fragment key={index}>
						{line}
						<br />
					</React.Fragment>
				))
			}</Typography>}
			<Divider varient="fullwidth" />
			<CardContent>
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					minHeight,
					maxHeight: 550,
					position: 'relative',
					'& .MuiDataGrid-root': {
						overflow: 'visible'
					}
				}}>
					<StyledDataGrid
						rows={data}
						columns={gridColumns}
						hideFooter
						apiRef={apiRef}
						slots={{
							toolbar: () => <CustomeToolBar onExport={handleExport} />
						}}
						initialState={{
							density: 'compact',
						}}
						columnHeaderHeight={69}
						pagination
						showToolbar
						paginationMode='client'
						paginationModel={{ page: state.currentPage, pageSize: state.pageSize }}
						pageSizeOptions={[5, 15, 25, 50]}
						autosizeOptions={{
							columns,
							includeHeaders: true,
							includeOutliers: true,
						}}
						slotProps={{
							// 🔹 affects the overall panel
							panel: {
								disablePortal: true,
								sx: {
									position: 'absolute',
								},
							},
							// 🔹 affects only the Columns panel (the one with checkboxes)
							columnsPanel: {
								sx: {
									maxHeight: data.length > 5 ? 300 : 200, // limit height
									overflowY: 'auto',    // add vertical scroll
									paddingRight: '8px',  // optional (avoid scrollbar overlaying checkboxes)
								},
							},
						}}
					/>
				</Box>
				<TablePagination
					component="div"
					count={data.length}
					page={state.currentPage}
					onPageChange={handlePageChange}
					rowsPerPage={state.pageSize}
					onRowsPerPageChange={handleRowsPerPage}
					rowsPerPageOptions={[5, 15, 25, 50]}
				/>
			</CardContent>
		</Card>
	</Dialog>
}

export default AdminLogsTable;